import { newScanAttemptId, type ScanConfirmation, ScanNotFound } from "@hobbyshopify/backend-client";
import { hasError } from "operation-result.js";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { loadScan, runScan } from "../../core/ops/scan-ops";
import { scanTitle } from "../../core/primitives/card-display";
import { errorText, thrownText } from "../../core/primitives/errors";
import { type EditState, inputToCents } from "../../core/primitives/scan-edit";
import { fileToDataUrl } from "../../core/primitives/scan-files";
import { applyScanEvent, initialScanProgress, SCAN_STAGES } from "../../core/primitives/scan-progress";
import { shopEntryId } from "../../core/primitives/workspace";
import { ScanEditorController, type ScanEditorDeps } from "../../sections/scan-editor/scan-editor-controller";
import { ScanCardActions, ScanCardState } from "./scan-card-model";

export type ScanCardDeps = ScanEditorDeps & Pick<AppDeps, "session">;

export interface ScanCardRouting {
  /** Keeps the scan under review in the URL, so a refresh reopens it; null clears it. */
  showScan(scanId: string | null): void;
}

/** The browser bits: object URLs for previews and reading a file for the API. Injectable because tests run without a DOM. */
export interface ScanCardIo {
  previewUrl(file: File): string;
  revokePreview(url: string): void;
  readImage(file: File): Promise<string>;
}

const browserIo: ScanCardIo = {
  previewUrl: (file) => URL.createObjectURL(file),
  revokePreview: (url) => URL.revokeObjectURL(url),
  readImage: fileToDataUrl,
};

/** Capture, scan with progress, review in the shared editor, done. */
export class ScanCardController extends SubtreeController implements ScanCardActions {
  readonly state: ScanCardState;
  readonly editor: ScanEditorController;
  #front: File | null = null;
  #back: File | null = null;
  #abort: AbortController | null = null;
  #restoreStarted = false;
  #attemptId: string | null = null;

  constructor(
    readonly deps: ScanCardDeps,
    readonly routing: ScanCardRouting,
    /** The scan the URL names, reopened for review after a refresh. */
    readonly restoreScanId: string | null = null,
    readonly io: ScanCardIo = browserIo,
    customerId: string | null = null,
  ) {
    super();
    this.state = new ScanCardState(customerId);
    this.subtree.put(ScanCardState, this.state);
    this.subtree.put(ScanCardActions, this);
    this.editor = this.own(
      new ScanEditorController(this.subtree, deps, {
        onConfirmed: (edit, result) => this.#confirmed(edit, result),
        onRejected: () => this.newScan(),
      }),
    );

    this.sync(() => {
      this.state.hasStore.value = deps.storeRepository.selectedStore !== null;
      const shopId = deps.storeRepository.shopId;
      if (restoreScanId && shopId !== null && !this.#restoreStarted) {
        this.#restoreStarted = true;
        void this.#restore(shopId, restoreScanId);
      } else if (!restoreScanId && shopId !== null && !this.#restoreStarted) {
        this.#restoreStarted = true;
        void this.#recoverPending(shopId);
      }
    }, [deps.storeRepository]);
    this.autoDispose(() => {
      this.#abort?.abort();
      this.#revokePreviews();
    });
  }

  setFront = (file: File) => {
    this.#attemptId = null;
    const previous = this.state.frontPreview.value;
    if (previous) this.io.revokePreview(previous);
    this.#front = file;
    this.state.frontPreview.value = this.io.previewUrl(file);
  };

  setBack = (file: File) => {
    this.#attemptId = null;
    const previous = this.state.backPreview.value;
    if (previous) this.io.revokePreview(previous);
    this.#back = file;
    this.state.backPreview.value = this.io.previewUrl(file);
  };

  toBackStep = () => {
    this.state.mobileSubstep.value = 2;
  };

  scan = async (skipBack = false) => {
    const front = this.#front;
    const shopId = this.deps.storeRepository.shopId;
    if (!front || shopId === null || this.state.step.value === 2) return;
    this.state.step.value = 2;
    this.state.scanError.value = null;
    this.state.scanProgress.value = initialScanProgress();
    this.#abort?.abort();
    const abort = new AbortController();
    this.#abort = abort;
    try {
      const imageUrl = await this.io.readImage(front);
      const backImageUrl = !skipBack && this.#back ? await this.io.readImage(this.#back) : undefined;
      if (this.isDisposed || this.#abort !== abort) return;
      // Streamed so the wait shows real stages instead of one opaque spinner:
      // identification and pricing are each several seconds.
      this.#attemptId ??= newScanAttemptId();
      const saved = await runScan({
        client: this.deps.client,
        session: this.deps.session,
        shopId,
        locationId: this.deps.storeRepository.locationId,
        images: {
          attemptId: this.#attemptId,
          imageUrl,
          ...(backImageUrl ? { backImageUrl } : {}),
          ...(this.state.customerId ? { customerId: this.state.customerId } : {}),
        },
        onProgress: (progress) => {
          if (!this.isDisposed && this.#abort === abort) {
            this.state.scanProgress.value = progress;
            if (progress.scanId) this.routing.showScan(progress.scanId);
          }
        },
        signal: abort.signal,
      });
      if (this.isDisposed || this.#abort !== abort) return;
      if (saved.recognitionOutcome === "no_match") this.state.noMatch.value = saved;
      else this.editor.seed(saved);
      this.state.step.value = 3;
      this.routing.showScan(saved.id);
    } catch (error) {
      if (this.isDisposed || this.#abort !== abort) return;
      if (this.state.scanProgress.value.status === "error") this.#attemptId = null;
      const stages = this.state.scanProgress.value.stages;
      const stage =
        SCAN_STAGES.find(({ key }) => stages[key] === "error") ??
        SCAN_STAGES.find(({ key }) => stages[key] === "active");
      const reason = thrownText(error, "Check your connection and try again.");
      this.state.scanError.value = stage ? `Scan stopped while ${stage.label.toLowerCase()}. ${reason}` : reason;
      this.state.step.value = 1;
      this.state.mobileSubstep.value = 1;
    } finally {
      if (this.#abort === abort) this.#abort = null;
    }
  };

  /** Stops waiting and returns to the photos. The backend still finishes and keeps the scan. */
  cancelScan = () => {
    if (this.state.step.value !== 2) return;
    const abort = this.#abort;
    this.#abort = null;
    abort?.abort();
    this.state.scanProgress.value = initialScanProgress();
    this.state.step.value = 1;
  };

  newScan = () => {
    this.#attemptId = null;
    this.state.noMatch.value = null;
    this.#abort?.abort();
    this.#abort = null;
    this.routing.showScan(null);
    this.#revokePreviews();
    this.#front = null;
    this.#back = null;
    this.state.step.value = 1;
    this.state.mobileSubstep.value = 1;
    this.state.frontPreview.value = null;
    this.state.backPreview.value = null;
    this.state.confirmed.value = null;
    this.state.scanError.value = null;
  };

  #confirmed(edit: EditState, result: ScanConfirmation): void {
    const scan = this.editor.state.scan.value;
    this.state.confirmed.value = {
      cardId: result.cardId,
      title: scan ? scanTitle(scan) : "Card",
      priceCents: inputToCents(edit.price) ?? null,
      // The lot id is the server's answer to "did this actually become stock",
      // which is the one thing the Done step cannot infer from the form.
      stocked: result.stockId !== null,
    };
    this.state.step.value = 5;
    this.routing.showScan(null);
  }

  async #recoverPending(shopId: string): Promise<void> {
    try {
      const ctx = await this.deps.session.authorizeCall();
      const entry = shopEntryId(shopId, this.deps.storeRepository.locationId);
      const pending = this.deps.client.scans.pendingAttempts(ctx, entry).at(-1);
      if (!pending || this.isDisposed || this.#front || this.state.step.value !== 1) return;
      this.#attemptId = pending.attemptId;
      const abort = new AbortController();
      this.#abort = abort;
      this.state.step.value = 2;
      for await (const event of this.deps.client.scans.recoverStream(ctx, entry, pending.attemptId, {
        signal: abort.signal,
      })) {
        if (this.isDisposed || abort.signal.aborted) return;
        this.state.scanProgress.value = applyScanEvent(this.state.scanProgress.value, event);
        if (event.type === "scan.started" && event.data.scanId) this.routing.showScan(event.data.scanId);
        if (event.type === "scan.completed") {
          this.routing.showScan(event.data.id);
          if (event.data.recognitionOutcome === "no_match") this.state.noMatch.value = event.data;
          else this.editor.seed(event.data);
          this.state.step.value = 3;
        }
        if (event.type === "scan.error") {
          this.#attemptId = null;
          this.state.scanError.value = event.data.error;
          this.state.step.value = 1;
        }
      }
    } catch (error) {
      if (!this.isDisposed && !this.#front) {
        this.state.scanError.value = thrownText(
          error,
          "Could not recover the previous scan. Check scan history before uploading again.",
        );
        this.state.step.value = 1;
      }
    }
  }

  async #restore(shopId: string, scanId: string): Promise<void> {
    this.state.restoring.value = true;
    let r = await loadScan({ ...this.deps, shopId, scanId });
    while (
      !this.isDisposed &&
      r.ok &&
      !r.value.cardInfo &&
      r.value.scanProgress !== "scan.completed" &&
      r.value.scanProgress !== "scan.error" &&
      r.value.review === "PENDING"
    ) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      if (this.isDisposed) return;
      r = await loadScan({ ...this.deps, shopId, scanId });
    }
    if (this.isDisposed) return;
    this.state.restoring.value = false;
    if (r.ok && r.value.recognitionOutcome === "no_match") {
      this.state.noMatch.value = r.value;
      this.state.step.value = 3;
      return;
    }
    // Only a pending scan is reviewable; anything else starts a fresh scan.
    if (r.ok && r.value.review === "PENDING") {
      this.editor.seed(r.value);
      this.state.step.value = 3;
      return;
    }
    this.routing.showScan(null);
    if (r.ok) return;
    this.state.scanError.value = hasError(r, ScanNotFound)
      ? "That scan is no longer available. Add photos to scan the card again."
      : `Couldn't reopen that scan. ${errorText(r.errors)}`;
  }

  #revokePreviews(): void {
    for (const url of [this.state.frontPreview.value, this.state.backPreview.value]) {
      if (url) this.io.revokePreview(url);
    }
  }
}
